import { useTranslation } from "react-i18next";
import styles from "../css/CollectionAddScreen.module.css";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import PlaceHolderTextField from "../../common/PlaceHolderTextField";
import BigButton from "../../common/BigButton";
import SpacingView from "../../common/view/SpaingView";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import { useEffect, useState } from "react";
import LoadingScreen from "../../common/LoadingScreen";
import { handleErrorAlert } from "../../common/OnevocaServerError";
import { patchCollectionName } from "../service/collectionAPI";
import Collection from "../model/Collection";
import ColorSelectScreen from "../../common/screen/ColorSelectScreen";
import CollectionColorSettingView from "../view/CollectionColorSettingView";
import { IconColor } from "../../common/model/IconColor";
import { IconColorSet } from "../../common/model/IconColorSet";
import { IconColorList } from "../../common/model/IconColorList";
import { useBackToClose } from "../../../hooks/useBackToClose";

interface CollectionEditScreenProps {
  open: boolean;
  collection?: Collection;
  onRename: () => void;
  onClose: () => void;
}

export default function CollectionEditScreen(props: CollectionEditScreenProps) {
  useBackToClose(props.open, props.onClose);
  const { t } = useTranslation();

  const [showLoading, setShowLoading] = useState(false);
  const [openColorSelectScreen, setOpenColorSelectScreen] = useState(false);

  const [collectionName, setCollectionName] = useState("");
  const [collectionColor, setCollectionColor] = useState<IconColor>(
    IconColor.from(IconColorSet.gray)
  );
  const [doneButtonStyle, setDoneButtonStyle] = useState<"normal" | "disable">(
    "disable"
  );

  useEffect(() => {
    if (props.collection === undefined) {
      return;
    }

    setCollectionName(props.collection.title);
    setCollectionColor(IconColor.from(props.collection.color));
    setDoneButtonStyle("normal");
  }, [props.open, props.collection]);

  if (!props.open) {
    return null;
  }

  function updateCollectionName(text: string) {
    setCollectionName(text);
  }

  function updateCollectionColor(color: IconColor) {
    setCollectionColor(color);
  }

  function updateDoneButtonStatus(text: string) {
    const length = text.length;
    if (length === 0 || length > 40) {
      setDoneButtonStyle("disable");
    } else {
      setDoneButtonStyle("normal");
    }
  }

  async function renameCollection() {
    if (props.collection === undefined) {
      return;
    }

    setShowLoading(true);
    try {
      await patchCollectionName(
        props.collection.id,
        collectionName,
        collectionColor.name
      );
      props.onRename();
      props.onClose();
    } catch (error) {
      handleErrorAlert(error);
    } finally {
      setShowLoading(false);
    }
  }

  function handleOnClickContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  if (props.collection === undefined || props.collection === null) return null;

  return (
    <>
      <div className={styles.stack} onClick={props.onClose}>
        <div className={styles.main}>
          <div className={styles.container} onClick={handleOnClickContainer}>
            <div className={styles.topContainer}>
              <ContainerNavigationView
                open={true}
                title={t("CollectionEditNavTitle")}
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={props.onClose}
              />
              <SpacingView height={24} />
              <PlaceHolderTextField
                text={props.collection.title}
                hint={props.collection.title}
                minCount={1}
                maxCount={40}
                lineStyle="single"
                autoFocus={true}
                handleTextChange={(text: string) => {
                  updateCollectionName(text);
                  updateDoneButtonStatus(text);
                }}
              />
              <SpacingView height={24} />
              <CollectionColorSettingView
                open={true}
                item={collectionColor}
                onClick={() => {
                  setOpenColorSelectScreen(true);
                }}
              />
            </div>
            <div className={styles.bottomContainer}>
              <BigButton
                title={t("Done")}
                variant={doneButtonStyle}
                handleClick={() => {
                  if (doneButtonStyle === "normal") {
                    renameCollection();
                  }
                }}
              />
            </div>
          </div>
        </div>
        <ColorSelectScreen
          open={openColorSelectScreen}
          onSelectItem={updateCollectionColor}
          list={IconColorList.collections}
          onClose={() => {
            setOpenColorSelectScreen(false);
          }}
        />
        {showLoading === true && <LoadingScreen />}
      </div>
    </>
  );
}
